'use client';

import { Popover, PopoverButton, PopoverPanel, RadioGroup } from '@headlessui/react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import BDRadio from '@shared/components/elements/bd-gold-radio';
import BDImage from '@shared/components/elements/bd-image';
import {
  CARAT_OPTIONS,
  CLARITY_OPTIONS,
  COLOR_OPTIONS,
  CUT_OPTIONS,
  PRICE_VALUES,
} from '@shared/lib/constants';
import { parseDiamondFilterParams } from '@shared/lib/overnight/vendor-utils';
import {
  clearUrlFilter,
  routeToPathKeepParams,
  setFilterUrlFilterChoice,
} from '@shared/lib/utils/url-utils';
import colorDImage from '@shared/public/images/color/Color_D.png';
import colorEImage from '@shared/public/images/color/Color_E.png';
import colorFImage from '@shared/public/images/color/Color_F.png';
import colorGImage from '@shared/public/images/color/Color_G.jpeg';
import colorHImage from '@shared/public/images/color/Color_H.jpeg';
import colorIImage from '@shared/public/images/color/Color_I.jpeg';
import colorJImage from '@shared/public/images/color/Color_J.jpeg';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { DiamondQualitativeRuler, DiamondQuantitativeRuler } from './diamond-4-c-ruler';
import { shapeImages } from './shape-images';
const colorImages = [
  colorJImage,
  colorIImage,
  colorHImage,
  colorGImage,
  colorFImage,
  colorEImage,
  colorDImage,
];

export default function DiamondFilter({
  origin,
  supportedShapes,
  onClose,
}: {
  origin: string;
  supportedShapes?: string[];
  onClose?: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    shape,
    clarityMin,
    clarityMax,
    cutMin,
    cutMax,
    colorMin,
    colorMax,
    caratMin,
    caratMax,
    priceMin,
    priceMax,
  } = parseDiamondFilterParams(searchParams);
  const setFilter = (key: string, value: string) => {
    setFilterUrlFilterChoice(router, pathname, searchParams, key, value, true, true, ['page']);
    onClose?.();
  };
  const clearFilter = (key: string) => {
    clearUrlFilter(router, pathname, searchParams, key, ['page']);
    onClose?.();
  };

  return (
    <div className="mt-4 flex flex-col gap-8 pb-20">
      <div key="origin" className="flex flex-col gap-2 border-t py-2">
        <h2 className="text-base font-medium">Diamond Origin</h2>
        <RadioGroup
          name="origin"
          id="origin"
          className="flex flex-col gap-2"
          value={origin}
          onChange={(value) => {
            routeToPathKeepParams(router, `/shop/diamonds/${value}`, searchParams);
          }}
        >
          <BDRadio value="bonded" className="py-2 text-sm">
            Personalized Bonded Diamond™
          </BDRadio>
          <BDRadio value="lab" className="py-2 text-sm">
            Lab Grown Diamonds
          </BDRadio>
          <BDRadio value="natural" className="py-2 text-sm">
            Natural Diamonds
          </BDRadio>
        </RadioGroup>
      </div>
      <div key="shape" className="flex flex-col gap-2 border-t py-2">
        <div className="flex flex-row items-center gap-2">
          <h4 className="text-normal flex flex-row items-center gap-2 font-medium">Shape</h4>
          <Popover className="relative">
            <PopoverButton className="flex items-center justify-center">
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </PopoverButton>
            <PopoverPanel className="bg-bd-light absolute left-1/2 top-full z-10 mt-2 w-80 -translate-x-1/2 transform rounded-sm p-2 text-sm shadow-sm">
              <p className="font-semibold">Shape (Cut) </p>
              <p className="">
                The shape of a polished diamond is also called diamond cut. Bonded Diamond™
                supports all the popular diamond shapes. The price difference among different shapes
                are mainly due to the differences of raw diamond size and the complexity of the
                cutting process.
              </p>
            </PopoverPanel>
          </Popover>
        </div>
        <RadioGroup
          name="shape"
          id="shape"
          className="@[42em]:grid-cols-5 grid grid-cols-4 gap-x-1 gap-y-1"
          value={shape?.toLowerCase()}
          onChange={(value) => {
            setFilter('shape', value);
            // scrollToCarat();
          }}
        >
          {Object.entries(shapeImages).map(([shape, image], idx) => (
            <BDRadio
              value={shape}
              key={shape}
              disabled={supportedShapes && !supportedShapes.includes(shape)}
              className="data-disabled:opacity-50"
            >
              <BDImage src={image} className="w-18 aspect-square" />

              <p className="text-xs">{shape}</p>
            </BDRadio>
          ))}
        </RadioGroup>
        {/* <div className="grid grid-cols-7">
          <p className="col-start-1 text-center text-xs text-bd-primary">Best Value</p>
          <p className="col-start-4 text-center text-xs text-bd-primary">Most Popular</p>
        </div> */}
      </div>

      <div key="price" className="flex flex-col gap-4 border-t py-2">
        <div className="flex flex-row items-center gap-2">
          <h4 className="text-normal font-medium">Price</h4>
        </div>
        <DiamondQuantitativeRuler
          values={PRICE_VALUES}
          lowLimit={priceMin}
          highLimit={priceMax}
          onSetLowLimitAction={(value) => {
            if (value === undefined) {
              clearFilter('priceMin');
            } else {
              setFilter('priceMin', value.toString());
            }
          }}
          onSetHighLimitAction={(value) => {
            if (value === undefined) {
              clearFilter('priceMax');
            } else {
              setFilter('priceMax', value.toString());
            }
          }}
        />
      </div>

      <div key="carat" className="flex flex-col gap-4 border-t py-2">
        <div className="flex flex-row items-center gap-2">
          <h4 className="text-normal font-medium">Carat</h4>
          <Popover className="relative">
            <PopoverButton className="flex items-center justify-center">
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </PopoverButton>
            <PopoverPanel className="absolute left-7 top-0 z-10 w-80 transform rounded-sm bg-gray-50 p-2 text-sm shadow-sm shadow-gray-200">
              <p className="font-semibold">Carat (ct.) </p>
              <p className="">
                The international unit of weight, used for measuring diamonds and gemstones. 1 carat
                is equal to 200 milligrams, or 0.2 grams.
              </p>
            </PopoverPanel>
          </Popover>
        </div>
        <DiamondQuantitativeRuler
          values={CARAT_OPTIONS}
          lowLimit={caratMin}
          highLimit={caratMax}
          onSetLowLimitAction={(value) => {
            if (value === undefined) {
              clearFilter('caratMin');
            } else {
              setFilter('caratMin', value.toString());
            }
          }}
          onSetHighLimitAction={(value) => {
            if (value === undefined) {
              clearFilter('caratMax');
            } else {
              setFilter('caratMax', value.toString());
            }
          }}
        />
      </div>
      <div key="color" className="flex flex-col gap-4 border-t py-2">
        <div className="flex flex-row items-center gap-2">
          <h4 className="text-normal font-medium">Color</h4>
          <Popover className="relative">
            <PopoverButton className="flex items-center justify-center">
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </PopoverButton>
            <PopoverPanel className="absolute left-7 top-0 z-10 w-96 transform rounded-sm bg-gray-50 p-2 text-sm shadow-sm shadow-gray-200">
              <p className="font-semibold">Diamond Color</p>
              <p className="">
                The color of a diamond is determined by the presence of trace elements in the
                diamond's crystal structure, with the D being the most colorless. F, E and D are
                "colorless" grade appreciated best set in platinum or white gold. I, H are
                "near-colorless" grade, excellent value set in yellow or rose gold.
              </p>
            </PopoverPanel>
          </Popover>
        </div>

        <DiamondQualitativeRuler
          values={COLOR_OPTIONS}
          lowLimit={colorMin}
          highLimit={colorMax}
          onSetLowLimitAction={(value) => {
            if (value === undefined) {
              clearFilter('colorMin');
            } else {
              setFilter('colorMin', value);
            }
          }}
          onSetHighLimitAction={(value) => {
            if (value === undefined) {
              clearFilter('colorMax');
            } else {
              setFilter('colorMax', value);
            }
          }}
        />
      </div>

      <div key="clarity" className="flex flex-col gap-4 border-t py-2">
        <div className="flex flex-row items-center gap-2">
          <h4 className="text-normal font-medium">Clarity</h4>
          <Popover className="relative">
            <PopoverButton className="flex items-center justify-center">
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </PopoverButton>
            <PopoverPanel className="absolute left-7 top-0 z-10 w-96 transform rounded-sm bg-gray-50 p-2 text-sm shadow-sm shadow-gray-200">
              <p className="font-semibold">Diamond Color</p>
              <p className="">
                The clarity of a diamond is determined by the presence of internal and external
                characteristics, with the FL being the flawless grade. VVS2, VVS1 are very, very
                slightly included grades. Characteristics miniscule and difficult to see under 10x
                magnification, even to a trained eye.
              </p>
            </PopoverPanel>
          </Popover>
        </div>
        <DiamondQualitativeRuler
          values={CLARITY_OPTIONS}
          lowLimit={clarityMin}
          highLimit={clarityMax}
          onSetLowLimitAction={(value) => {
            if (value === undefined) {
              clearFilter('clarityMin');
            } else {
              setFilter('clarityMin', value);
            }
          }}
          onSetHighLimitAction={(value) => {
            if (value === undefined) {
              clearFilter('clarityMax');
            } else {
              setFilter('clarityMax', value);
            }
          }}
        />
      </div>

      <div key="cut" className="flex flex-col gap-4 border-t py-2">
        <div className="flex flex-row items-center gap-2">
          <h4 className="text-normal font-medium">Cut</h4>
          <Popover className="relative">
            <PopoverButton className="flex items-center justify-center">
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </PopoverButton>
            <PopoverPanel className="absolute left-7 top-0 z-10 w-96 transform rounded-sm bg-gray-50 p-2 text-sm shadow-sm shadow-gray-200">
              <p className="font-semibold">Diamond Color</p>
              <p className="">
                The cut represents the quality of a diamond's proportions, symmetry, and polish. The
                ideal cut is the most symmetrical and well-proportioned diamond, resulting in
                maximum sparkle and fire.
              </p>
            </PopoverPanel>
          </Popover>
        </div>
        <DiamondQualitativeRuler
          values={CUT_OPTIONS}
          lowLimit={cutMin}
          highLimit={cutMax}
          onSetLowLimitAction={(value) => {
            if (value === undefined) {
              clearFilter('cutMin');
            } else {
              setFilter('cutMin', value);
            }
          }}
          onSetHighLimitAction={(value) => {
            if (value === undefined) {
              clearFilter('cutMax');
            } else {
              setFilter('cutMax', value);
            }
          }}
        />
      </div>
    </div>
  );
}
